import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { observer } from "mobx-react-lite";
import { HomeContainer } from "@client/pages/Home/Home.styled";
import { GlobalLoader } from "@common/components/GlobalLoader/GlobalLoader";
import { useStores } from "@client/store";
import {
  CreateCampaignsControl,
  CreateCampaignTabs,
  CreateCampaignWrapper,
} from "@admin/pages/CreateCampaign/CreateCampaign.styled";
import { Block, Spacer, TabSection, Typography, useTheme } from "@ugc-ui/lego";
import { useHorizontalScroll } from "@common/hooks/useHorizontalScroll";
import { CreateCampaignTabView } from "@client/pages/CreateCampaign/Tabs/CreateCampaignTabView";
import { CreateCampaignTabStatistic } from "@client/pages/CreateCampaign/Tabs/CreateCampaignTabStatistic";
import { useIsMobile } from "@common/hooks/isMobile";
import { Header } from "@client/pages/Home/Header/Header";
import { ModalVideoPlayer } from "@client/components/ModalVideoPlayer/ModalVideoPlayer";
import { ConfirmActionModal } from "@common/components/Modal/ConfirmActionModal/ConfirmActionModal";
import { NoticeNotAvailableAction } from "@common/components/Notice/NoticeNotAvailableAction/NoticeNotAvailableAction";
import { DrawerUploadVideoMenu } from "@client/components/DrawerUploadVideoMenu/DrawerUploadVideoMenu";
import { ButtonBack } from "@common/components/ButtonBack/ButtonBack";
import { ConfirmActionModalType } from "@common/components/Modal/ConfirmActionModal/ConfirmActionModal.utils";
import { VideoUploadErrorModal } from "@common/components/ModalVideoUploadError/VideoUploadErrorModal";
import { InitialDialog } from "../Home/InitialDialog/InitialDialog";

export enum CreateCampaignGroup {
  VIEW = "view",
  STATISTIC = "statistic",
}

export const CreateCampaignPage = observer(() => {
  const theme = useTheme();
  const {
    exitModalStore,
    client: { uploadAreaStore, notAvailableActionNotice },
    uploadAreaErrorModal,
  } = useStores();
  const isMobile = useIsMobile();

  const ref = useHorizontalScroll<HTMLDivElement>();

  const [tab, setTab] = useState(CreateCampaignGroup.VIEW);
  const handleClickOnCampaignTab = (newTab) => setTab(newTab);

  const TAB_SECTION_STYLE = {
    minWidth: "unset",
    flexShrink: 0,
    overflow: "hidden",
  };

  return (
    <>
      <Helmet>
        <title>Рекламный кабинет - создание кампании</title>
      </Helmet>

      {!isMobile && <Header />}
      <Block dataCy="страница создания рекламной кампании">
        <HomeContainer>
          <Spacer size={24} vertical />
          <CreateCampaignWrapper>
            <div>
              <ButtonBack
                title="Новая рекламная кампания"
                type={ConfirmActionModalType.EXIT}
                exitModalStore={exitModalStore}
              />

              <CreateCampaignsControl theme={theme}>
                <CreateCampaignTabs
                  ref={ref}
                  dataCy="контейнер-табов"
                  value={tab}
                  onChange={handleClickOnCampaignTab}
                  padding="0"
                  noBackground
                  underlineHeight={1}
                >
                  <TabSection
                    label="Обзор"
                    value={CreateCampaignGroup.VIEW}
                    style={TAB_SECTION_STYLE}
                  />
                  <TabSection
                    label="Статистика"
                    value={CreateCampaignGroup.STATISTIC}
                    style={TAB_SECTION_STYLE}
                  />
                </CreateCampaignTabs>

                {!isMobile && (
                  <Typography type="subtitle2" color={theme.gray.a40}>
                    Черновик
                  </Typography>
                )}
              </CreateCampaignsControl>
            </div>
            <Spacer size={28} vertical />
            {tab === CreateCampaignGroup.VIEW ? (
              <CreateCampaignTabView />
            ) : (
              <CreateCampaignTabStatistic />
            )}
          </CreateCampaignWrapper>
        </HomeContainer>
      </Block>
      <GlobalLoader progress={uploadAreaStore.loadingProgress} />
      <ModalVideoPlayer />
      <ConfirmActionModal exitModalStore={exitModalStore} />
      <NoticeNotAvailableAction
        isOpen={notAvailableActionNotice.isOpen}
        // eslint-disable-next-line react/jsx-handler-names
        onCloseDialog={notAvailableActionNotice.closeDialog}
      />
      <DrawerUploadVideoMenu uploadAreaDetach={uploadAreaStore.detach} />
      <InitialDialog />
      <VideoUploadErrorModal uploadAreaErrorModalStore={uploadAreaErrorModal} />
    </>
  );
});
